import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StandardPageSkeleton } from '../../components/common/Skeleton';
import { PageFadeSection } from '../../components/common/PageFadeSection';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/axios';
import { getProductionLotByTaskId, formatDuration, calculateTotalPlannedHours } from '../../api/sapOData.api';
import { getProductionLotById, getProductionLotsBySpecificationId, getSalesOrderNoByOrderId, getStatusDescription, getStatusColorClass } from '../../api/productionLot.api';
import {
  getProductionLotSummaries,
  getProductionTaskById,
  createProductionLot,
  updateTaskAssignments,
  syncProductionTasksFromSap
} from '../../api/productionTask.api';
import { getUserLookup } from '../../api/adminApi';
import { getActiveTeamLookups, getTeams } from '../../api/teamApi';
import AssignTaskModal from '../../components/tasks/AssignTaskModal';
import ProductionTaskCard from '../../components/tasks/ProductionTaskCard';
import ProductionTaskDetailModal from '../../components/tasks/ProductionTaskDetailModal';

import {
  Settings,
  Plus,
  Search,
  Filter,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  Clock,
  Package,
  BarChart3,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  Upload,
  Image as ImageIcon,
  Trash2,
  Loader2,
  ExternalLink,
  Activity,
  Timer,
  Boxes,
  Eye,
  FileText,
  QrCode,
  Factory,
  Clipboard,
  Download,
  Printer,
  Hash,
  Users,
  Grid,
  List,
  Building,
  Database,
  Tag,
  Calendar,
  CalendarClock,
  Layers,
  User,
  ChevronUp,
  SlidersHorizontal,
  Sparkles,
  Zap,
  ShoppingCart
} from 'lucide-react';

const ROLE_ADMIN = 4;
const ROLE_PM = 3;
const ROLE_LEAD = 2;

const ProductionTasksPage = () => {
  const { getAccessToken, user: authUser, setSyncInBackground: globalSetSyncInBackground } = useAuth();
  const { t } = useTranslation();
  const userRole = authUser?.role ?? 0;
  const isAdmin = userRole >= ROLE_ADMIN;
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('card');
  const notificationOpenRef = useRef(false);

  const isInitialMount = useRef(true);
  const fetchLock = useRef(false);
  /** After first fetch, use inline list loading instead of full-page skeleton */
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [statusCounts, setStatusCounts] = useState({ 'in-progress': 0, completed: 0, 'not-started': 0 });

  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isTaskDetailModalOpen, setIsTaskDetailModalOpen] = useState(false);

  const [isSapPreviewOpen, setIsSapPreviewOpen] = useState(false);
  const [sapPreviewId, setSapPreviewId] = useState('');
  const [sapPreviewData, setSapPreviewData] = useState(null);
  const [sapPreviewLoading, setSapPreviewLoading] = useState(false);
  const [sapPreviewError, setSapPreviewError] = useState(null);
  const [lookupMode, setLookupMode] = useState('taskId');

  // Sales Order search can yield multiple results — user picks one
  const [specResults, setSpecResults] = useState(null);
  const [specSelectedOrder, setSpecSelectedOrder] = useState(null);

  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [assignTaskData, setAssignTaskData] = useState(null);

  const [sapTaskId, setSapTaskId] = useState('');
  const [sapLotData, setSapLotData] = useState(null);
  const [sapLoading, setSapLoading] = useState(false);
  const [sapError, setSapError] = useState(null);

  // New state for detail modal with API fetching
  const [detailModalLoading, setDetailModalLoading] = useState(false);
  const [detailedTask, setDetailedTask] = useState(null);

  // Cached users and teams - fetched once, shared across modals
  const [cachedUsers, setCachedUsers] = useState([]);
  const [cachedTeams, setCachedTeams] = useState([]);

  // Sync from SAP state
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncInBackground, setSyncInBackground] = useState(false);

  // Modern Filter States - These are the actual values sent to API
  const [filterProductionTaskId, setFilterProductionTaskId] = useState('');
  const [filterProductionLotId, setFilterProductionLotId] = useState('');
  const [filterSalesOrderNo, setFilterSalesOrderNo] = useState('');
  // Status Filter - using 'Status' parameter as per backend C# class
  const [filterStatus, setFilterStatus] = useState('');

  // UI Input States (debounced)
  const [localFilterProductionTaskId, setLocalFilterProductionTaskId] = useState('');
  const [localFilterProductionLotId, setLocalFilterProductionLotId] = useState('');
  const [localFilterSalesOrderNo, setLocalFilterSalesOrderNo] = useState('');

  // Main search term
  const [searchTerm, setSearchTerm] = useState('');
  const [localSearchTerm, setLocalSearchTerm] = useState('');

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [activeFilterCount, setActiveFilterCount] = useState(0);

  // Debounce timers
  const mainSearchDebounceRef = useRef(null);
  const taskIdDebounceRef = useRef(null);
  const lotIdDebounceRef = useRef(null);
  const salesOrderDebounceRef = useRef(null);

  // Count active filters based on ACTUAL filter values (not local)
  useEffect(() => {
    let count = 0;
    if (filterProductionTaskId && filterProductionTaskId.trim()) count++;
    if (filterProductionLotId && filterProductionLotId.trim()) count++;
    if (filterSalesOrderNo && filterSalesOrderNo.trim()) count++;
    if (filterStatus && filterStatus.trim()) count++;
    setActiveFilterCount(count);
  }, [filterProductionTaskId, filterProductionLotId, filterSalesOrderNo, filterStatus]);

  // Clear all filters
  const clearAllFilters = () => {
    // Clear local input states
    setLocalFilterProductionTaskId('');
    setLocalFilterProductionLotId('');
    setLocalFilterSalesOrderNo('');
    setLocalSearchTerm('');
    // Clear actual filter states
    setFilterProductionTaskId('');
    setFilterProductionLotId('');
    setFilterSalesOrderNo('');
    setFilterStatus('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Main search debounce
  useEffect(() => {
    if (mainSearchDebounceRef.current) {
      clearTimeout(mainSearchDebounceRef.current);
    }

    mainSearchDebounceRef.current = setTimeout(() => {
      setSearchTerm(localSearchTerm);
      setCurrentPage(1);
    }, 600);

    return () => {
      if (mainSearchDebounceRef.current) {
        clearTimeout(mainSearchDebounceRef.current);
      }
    };
  }, [localSearchTerm]);

  // Filter: Production Task ID debounce
  useEffect(() => {
    if (taskIdDebounceRef.current) {
      clearTimeout(taskIdDebounceRef.current);
    }

    taskIdDebounceRef.current = setTimeout(() => {
      setFilterProductionTaskId(localFilterProductionTaskId);
      setCurrentPage(1);
    }, 600);

    return () => {
      if (taskIdDebounceRef.current) {
        clearTimeout(taskIdDebounceRef.current);
      }
    };
  }, [localFilterProductionTaskId]);

  // Filter: Production Lot ID debounce
  useEffect(() => {
    if (lotIdDebounceRef.current) {
      clearTimeout(lotIdDebounceRef.current);
    }

    lotIdDebounceRef.current = setTimeout(() => {
      setFilterProductionLotId(localFilterProductionLotId);
      setCurrentPage(1);
    }, 600);

    return () => {
      if (lotIdDebounceRef.current) {
        clearTimeout(lotIdDebounceRef.current);
      }
    };
  }, [localFilterProductionLotId]);

  // Filter: Sales Order No debounce
  useEffect(() => {
    if (salesOrderDebounceRef.current) {
      clearTimeout(salesOrderDebounceRef.current);
    }

    salesOrderDebounceRef.current = setTimeout(() => {
      setFilterSalesOrderNo(localFilterSalesOrderNo);
      setCurrentPage(1);
    }, 600);

    return () => {
      if (salesOrderDebounceRef.current) {
        clearTimeout(salesOrderDebounceRef.current);
      }
    };
  }, [localFilterSalesOrderNo]);

  // Fetch on page change, page size change, status filter, and actual filter values
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchTasks();
    } else {
      fetchTasks();
    }
  }, [currentPage, pageSize, filterProductionTaskId, filterProductionLotId, filterSalesOrderNo, filterStatus, searchTerm]);

  // Fetch users and teams once on mount - role-filtered for PM/Lead
  useEffect(() => {
    const fetchUsersAndTeams = async () => {
      if (isAdmin) {
        try {
          const usersRes = await getUserLookup();
          const usersData = usersRes?.data || usersRes || [];
          setCachedUsers(Array.isArray(usersData) ? usersData.map(u => ({ id: u.id, name: u.name })) : []);
        } catch (e) { setCachedUsers([]); }
        try {
          const teamsRes = await getActiveTeamLookups();
          const teamsData = Array.isArray(teamsRes) ? teamsRes : teamsRes?.data || [];
          setCachedTeams(teamsData);
        } catch (e) { setCachedTeams([]); }
      } else if (userRole === ROLE_PM || userRole === ROLE_LEAD) {
        try {
          const teamsRes = await getTeams(null, true);
          const allTeams = teamsRes?.data || teamsRes || [];
          const teamList = Array.isArray(allTeams) ? allTeams : [];

          let myTeams = [];
          if (userRole === ROLE_PM) {
            myTeams = teamList.filter(t => t.pmUserId === authUser?.id);
          } else {
            myTeams = teamList.filter(t => t.leadUserId === authUser?.id);
          }

          const allowedUsers = new Map();
          if (authUser?.id) {
            allowedUsers.set(authUser.id, authUser.firstName && authUser.lastName
              ? `${authUser.firstName} ${authUser.lastName}`.trim()
              : authUser.name || authUser.email);
          }
          myTeams.forEach(team => {
            if (userRole === ROLE_PM && team.leadUserId && team.leadName) {
              allowedUsers.set(team.leadUserId, team.leadName);
            }
            (team.members || []).filter(m => m.isActive !== false).forEach(m => {
              allowedUsers.set(m.userId, m.fullName || m.name || m.email);
            });
          });

          setCachedUsers(Array.from(allowedUsers, ([id, name]) => ({ id, name })));
          setCachedTeams(myTeams.map(t => ({ id: t.id, name: t.name })));
        } catch (e) {
          console.error('Failed to fetch role-filtered users/teams:', e);
          setCachedUsers([]);
          setCachedTeams([]);
        }
      } else {
        if (authUser?.id) {
          setCachedUsers([{ id: authUser.id, name: authUser.name || authUser.email }]);
        }
        setCachedTeams([]);
      }
    };
    fetchUsersAndTeams();
  }, [authUser?.id, userRole]);

  const fetchTasks = async () => {
    if (fetchLock.current) {
      return;
    }

    fetchLock.current = true;
    setLoading(true);

    try {
      const params = {
        pageNumber: currentPage,
        pageSize: pageSize,
      };

      // Add filters only if they have values
      if (filterProductionTaskId && filterProductionTaskId.trim()) {
        params.productionTaskId = filterProductionTaskId.trim();
      }
      if (filterProductionLotId && filterProductionLotId.trim()) {
        params.productionLotId = filterProductionLotId.trim();
      }
      if (filterSalesOrderNo && filterSalesOrderNo.trim()) {
        params.salesOrderNo = filterSalesOrderNo.trim();
      }
      // IMPORTANT: Use 'Status' parameter name (matching C# ProductionLotFilter class)
      if (filterStatus && filterStatus.trim()) {
        params.Status = filterStatus.trim();
      }
      if (searchTerm && searchTerm.trim()) {
        params.searchTerm = searchTerm.trim();
      }

      console.log('Fetching tasks with params:', params);

      const response = await getProductionLotSummaries(params);

      console.log('Production Lot Summaries Response:', response);

      let items = [];
      let total = 0;
      let pages = 0;
      let serverStatusCounts = null;

      if (response.data && Array.isArray(response.data)) {
        items = response.data;
        total = response.totalCount || response.data.length;
        pages = response.totalPages || Math.ceil(total / pageSize);
        serverStatusCounts = response.statusCounts;
      } else if (Array.isArray(response)) {
        items = response;
        total = response.length;
        pages = Math.ceil(total / pageSize);
      } else if (response.items && Array.isArray(response.items)) {
        items = response.items;
        total = response.totalCount || response.items.length;
        pages = response.totalPages || Math.ceil(total / pageSize);
        serverStatusCounts = response.statusCounts;
      }

      const normalizedTasks = items.map((task) => ({
        id: task.id || task.productionLotId || task.productionTaskId,
        productionLotId: task.productionLotId,
        productionOrderId: task.productionOrderId,
        productionLotUUID: task.productionLotUUID,
        productionOrderUUID: task.productionOrderUUID,
        title: task.title || task.description || `Production Lot ${task.productionLotId}`,
        description: task.description || task.mainOutputProductDescription || t('pages.productionTasks.noDescription'),
        barcodeNumber: task.barcodeNumber || task.barcodeContentValue || task.productionLotId,
        barcodeBase64: task.barcodeBase64,
        status: mapStatus(task.lifeCycleStatusCode || task.status),
        lifeCycleStatusCode: task.lifeCycleStatusCode,
        lifeCycleStatusDescription: task.lifeCycleStatusDescription,
        isActive: task.isActive !== undefined ? task.isActive : true,
        userName: task.userName || task.assignedUser,
        teamName: task.teamName,
        createdAt: task.createdAt,
        siteId: task.siteId,
        siteDescription: task.siteDescription,
        totalQuantity: task.totalQuantity,
        totalQuantityUnit: task.totalQuantityUnit,
        totalCompletedQuantity: task.totalCompletedQuantity,
        totalCompletedQuantityUnit: task.totalCompletedQuantityUnit,
        productionStartDate: task.productionStartDate,
        productionEndDate: task.productionEndDate,
        mainOutputProductId: task.mainOutputProductId,
        mainOutputProductDescription: task.mainOutputProductDescription,
        confirmationGroups: task.confirmationGroups || [],
        productionTasks: task.productionTasks || [],
        dbProductionTasks: task.dbProductionTasks || task.productionTasks || [],
        salesOrderNo: task.salesOrderNo || null
      }));

      setTasks(normalizedTasks);
      setTotalItems(total);
      setTotalPages(pages);
      if (serverStatusCounts) {
        setStatusCounts(serverStatusCounts);
      }

    } catch (error) {
      console.error('Failed to fetch production lots:', error);
      setTasks([]);
      setTotalItems(0);
      setTotalPages(0);
      setStatusCounts({ 'in-progress': 0, completed: 0, 'not-started': 0 });
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
      setTimeout(() => {
        fetchLock.current = false;
      }, 100);
    }
  };

  // Auto-open task detail when navigating from a notification
  useEffect(() => {
    const searchFromUrl = searchParams.get('search');
    const shouldOpenTask = searchParams.get('openTask') === 'true';

    if (searchFromUrl && shouldOpenTask && !loading && tasks.length > 0 && !notificationOpenRef.current) {
      notificationOpenRef.current = true;

      const matchedTask = tasks.find(t =>
        t.productionLotId === searchFromUrl || t.productionOrderId === searchFromUrl
      );

      if (matchedTask) {
        openTaskDetailModal(matchedTask);
      }

      setSearchParams({}, { replace: true });
    }
  }, [loading, tasks, searchParams]);

  const handleImportTask = async () => {
    if (!sapPreviewData) return;

    setSapPreviewLoading(true);
    setSapPreviewError(null);

    try {
      let taskAssignments = [];
      if (!isAdmin && authUser?.id && (userRole === ROLE_PM || userRole === ROLE_LEAD)) {
        const tasks = sapPreviewData.productionTasks || [];
        const cgTasks = (sapPreviewData.confirmationGroups || [])
          .flatMap(cg => cg.productionTasks || []);
        const allTasks = [...tasks, ...cgTasks];
        const seen = new Set();
        allTasks.forEach(t => {
          const id = t.productionTaskId;
          if (id && !seen.has(id)) {
            seen.add(id);
            taskAssignments.push({ productionTaskId: id, userId: authUser.id, teamId: null });
          }
        });
        if (taskAssignments.length === 0) {
          taskAssignments.push({ productionTaskId: null, userId: authUser.id, teamId: null });
        }
      }

      const payload = {
        lot: sapPreviewData,
        taskAssignments
      };

      console.log('Importing task with payload:', payload);

      const response = await createProductionLot(payload);

      toast.success(
        <div>
          <strong className="block mb-1">{t('pages.productionTasks.importSuccess')}</strong>
          <span className="text-sm opacity-90">
            {t('pages.productionTasks.importedTaskId', { id: sapPreviewData.productionLotId })}
            {!isAdmin && t('pages.productionTasks.assignedToYou')}
          </span>
        </div>,
        { duration: 3000, icon: '✅' }
      );

      await fetchTasks();

      setTimeout(() => {
        closeSapPreviewModal();
      }, 1500);

    } catch (error) {
      console.error('❌ Import failed:', error);

      if (error.isDuplicate || error.message?.includes('already exists')) {
        const errorMsg = t('pages.productionTasks.taskAlreadyExists', { id: sapPreviewData.productionLotId });
        setSapPreviewError(errorMsg);
        toast.error(
          <div>
            <strong className="block mb-1">{t('pages.productionTasks.duplicateTask')}</strong>
            <span className="text-sm">{errorMsg}</span>
          </div>,
          { duration: 4000 }
        );
      } else {
        const errorMsg = error.message || t('pages.productionTasks.importFailedTryAgain');
        setSapPreviewError(errorMsg);
        toast.error(
          <div>
            <strong className="block mb-1">{t('pages.productionTasks.importFailed')}</strong>
            <span className="text-sm">{errorMsg}</span>
          </div>,
          { duration: 4000 }
        );
      }
    } finally {
      setSapPreviewLoading(false);
    }
  };

  const handleSyncFromSap = async () => {
    setSyncLoading(true);
    setSyncInBackground(false);
    globalSetSyncInBackground(false);
    try {
      const result = await syncProductionTasksFromSap();
      const data = result.data;

      if (data?.imported > 0) {
        toast.success(
          <div>
            <strong className="block mb-1">{t('pages.productionTasks.sapSyncCompleteTitle')}</strong>
            <span className="text-sm opacity-90">
              {t('pages.productionTasks.sapSyncImportedLine', {
                imported: data.imported,
                skippedLine: data.skipped > 0 ? t('pages.productionTasks.skippedAlready', { skipped: data.skipped }) : ''
              })}
            </span>
          </div>,
          { duration: 4000, icon: '🔄' }
        );
        await fetchTasks();
      } else if (data?.totalFound === 0) {
        toast(
          <div>
            <strong className="block mb-1">{t('pages.productionTasks.noTasksFound')}</strong>
            <span className="text-sm opacity-90">
              {t('pages.productionTasks.noTasksBody')}
            </span>
          </div>,
          { duration: 3000, icon: 'ℹ️' }
        );
      } else {
        toast(
          <div>
            <strong className="block mb-1">{t('pages.productionTasks.alreadyUpToDate')}</strong>
            <span className="text-sm opacity-90">
              {t('pages.productionTasks.upToDateBody', { count: data?.skipped || 0 })}
            </span>
          </div>,
          { duration: 3000, icon: '✅' }
        );
      }

      if (data?.errors?.length > 0) {
        console.warn('SAP sync errors:', data.errors);
      }
    } catch (error) {
      console.error('SAP sync failed:', error);
      toast.error(
        <div>
          <strong className="block mb-1">{t('pages.productionTasks.syncFailed')}</strong>
          <span className="text-sm">{error.message || t('pages.productionTasks.syncFailedMsg')}</span>
        </div>,
        { duration: 4000 }
      );
    } finally {
      setSyncLoading(false);
      setSyncInBackground(false);
      globalSetSyncInBackground(false);
    }
  };

  const handleContinueInBackground = () => {
    setSyncLoading(false);
    setSyncInBackground(true);
    globalSetSyncInBackground(true);
    toast(t('pages.productionTasks.syncingBackground'), { icon: '🔄', duration: 3000 });
  };

  const mapStatus = (statusCode) => {
    if (!statusCode) return 'not-started';
    const code = statusCode.toString();
    switch (code) {
      case '4': return 'completed';
      case '3': return 'in-progress';
      case '2': return 'in-progress';
      default: return 'not-started';
    }
  };

  const handleSapTaskLookup = async () => {
    if (!sapTaskId.trim()) {
      setSapError(t('pages.productionTasks.taskNotFound'));
      setSapLotData(null);
      return;
    }

    setSapLoading(true);
    setSapError(null);
    setSapLotData(null);

    try {
      const result = await getProductionLotByTaskId(sapTaskId.trim());

      if (result.isSuccess && result.data) {
        setSapLotData(result.data);
        setSapError(null);
      } else {
        setSapError(result.message || t('pages.productionTasks.taskNotFoundInSap'));
        setSapLotData(null);
      }
    } catch (error) {
      console.error('SAP lookup failed:', error);
      setSapError(error.response?.data?.message || t('pages.productionTasks.fetchFailed'));
      setSapLotData(null);
    } finally {
      setSapLoading(false);
    }
  };

  const clearSapLookup = () => {
    setSapTaskId('');
    setSapLotData(null);
    setSapError(null);
  };

  const openSapPreviewModal = () => {
    setIsSapPreviewOpen(true);
    setSapPreviewId('');
    setSapPreviewData(null);
    setSapPreviewError(null);
  };

  const closeSapPreviewModal = () => {
    setIsSapPreviewOpen(false);
    setSapPreviewId('');
    setSapPreviewData(null);
    setSapPreviewError(null);
    setSpecResults(null);
    setSpecSelectedOrder(null);
  };

  const handleTaskAssigned = (result) => {
    console.log('Task assigned successfully:', result);
    fetchTasks();
  };

  const handleSapPreviewLookup = async () => {
    if (!sapPreviewId.trim()) {
      const labels = {
        taskId: t('pages.productionTasks.taskIdMode'),
        orderId: t('pages.productionTasks.orderIdMode'),
        salesOrder: t('pages.productionTasks.salesOrderNo')
      };
      setSapPreviewError(t('pages.productionTasks.pleaseEnterField', { field: labels[lookupMode] }));
      return;
    }

    setSapPreviewLoading(true);
    setSapPreviewError(null);
    setSapPreviewData(null);
    setSpecResults(null);
    setSpecSelectedOrder(null);

    try {
      if (lookupMode === 'salesOrder') {
        const result = await getProductionLotsBySpecificationId(sapPreviewId.trim());

        if (result.isSuccess && result.data && result.data.length > 0) {
          setSpecResults(result.data);
          setSapPreviewError(null);
        } else {
          setSapPreviewError(result.message || t('pages.productionTasks.noLotsForSalesOrder', { id: sapPreviewId.trim() }));
        }
      } else {
        const result = await getProductionLotById(
          sapPreviewId.trim(),
          true,
          true,
          lookupMode === 'taskId'
        );

        if (result.isSuccess && result.data) {
          const orderId = result.data.productionOrderId;
          if (orderId) {
            try {
              const soResult = await getSalesOrderNoByOrderId(orderId);
              if (soResult.isSuccess && soResult.data) {
                result.data.salesOrderNo = soResult.data;
              }
            } catch (e) {
              console.warn('Could not fetch Sales Order No:', e);
            }
          }
          setSapPreviewData(result.data);
          setSapPreviewError(null);
        } else {
          setSapPreviewError(result.message || t('pages.productionTasks.lookupNotFoundInSap', {
            field: lookupMode === 'taskId' ? t('pages.productionTasks.productionLot') : t('pages.productionTasks.productionOrder')
          }));
        }
      }
    } catch (error) {
      console.error('SAP Preview lookup failed:', error);
      setSapPreviewError(error.response?.data?.message || t('pages.productionTasks.fetchFailed'));
    } finally {
      setSapPreviewLoading(false);
    }
  };

  const handleSpecCardSelect = async (order) => {
    setSpecSelectedOrder(order);
    setSapPreviewLoading(true);
    setSapPreviewError(null);

    try {
      const orderId = order.productionOrderId;
      if (!orderId) {
        setSapPreviewError(t('pages.productionTasks.noProductionOrderIdOnResult'));
        setSapPreviewLoading(false);
        return;
      }

      const result = await getProductionLotById(orderId, true, true, false);

      if (result.isSuccess && result.data) {
        result.data.salesOrderNo = order.productionSpecificationId || sapPreviewId.trim();
        setSapPreviewData(result.data);
        setSpecResults(null);
      } else {
        setSapPreviewError(result.message || t('pages.productionTasks.failedLoadOrderDetails', { id: orderId }));
      }
    } catch (error) {
      console.error('Spec card detail load failed:', error);
      setSapPreviewError(error.response?.data?.message || t('pages.productionTasks.failedFetchLotDetails'));
    } finally {
      setSapPreviewLoading(false);
    }
  };

  const openTaskDetailModal = async (task) => {
    setDetailModalLoading(true);
    setIsTaskDetailModalOpen(true);
    setDetailedTask(null);

    try {
      const productionLotId = task.productionLotId || task.id;
      if (productionLotId) {
        const response = await getProductionTaskById(productionLotId);

        let detailedData = null;

        if (response?.data) {
          detailedData = response.data;
        } else if (response?.isSuccess && response?.data) {
          detailedData = response.data;
        } else {
          detailedData = response;
        }

        setDetailedTask(detailedData);
      } else {
        setDetailedTask(task);
      }
    } catch (error) {
      setDetailedTask(task);
    } finally {
      setDetailModalLoading(false);
    }
  };

  const closeTaskDetailModal = () => {
    setIsTaskDetailModalOpen(false);
    setDetailedTask(null);
  };

  const handlePrintReport = () => {
    if (!sapPreviewData) return;
    const printContent = generateReportHTML(sapPreviewData);
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const handleDownloadReport = () => {
    if (!sapPreviewData) return;
    const reportContent = generateReportHTML(sapPreviewData);
    const blob = new Blob([reportContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ProductionLot_${sapPreviewData.productionLotId}_Report.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const generateReportHTML = (data) => {
    const allActivities = data.confirmationGroups?.flatMap(group =>
      group.activities?.map(activity => ({
        ...activity,
        confirmationGroupId: group.confirmationGroupId
      })) || []
    ) || [];

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${t('pages.productionTasks.productionLotReportTitle')} - ${data.productionLotId}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 40px; background: #fff; color: #333; }
          .header { background: linear-gradient(135deg, #4f46e5, #7c3aed); color: white; padding: 30px; border-radius: 12px; margin-bottom: 30px; }
          .header h1 { font-size: 28px; margin-bottom: 8px; }
          .header p { opacity: 0.9; font-size: 14px; }
          .report-meta { display: flex; justify-content: space-between; margin-top: 15px; font-size: 12px; opacity: 0.8; }
          .section { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 24px; margin-bottom: 24px; }
          .section-title { font-size: 16px; font-weight: 600; color: #1e293b; margin-bottom: 16px; padding-bottom: 12px; border-bottom: 2px solid #e2e8f0; display: flex; align-items: center; gap: 8px; }
          .section-title::before { content: ''; width: 4px; height: 20px; background: #4f46e5; border-radius: 2px; }
          .info-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px; }
          .info-item { background: white; padding: 16px; border-radius: 8px; border: 1px solid #e2e8f0; }
          .info-label { font-size: 11px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; }
          .info-value { font-size: 16px; font-weight: 600; color: #1e293b; }
          .status-badge { display: inline-block; padding: 6px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; }
          .status-closed { background: #dcfce7; color: #166534; }
          .status-progress { background: #dbeafe; color: #1e40af; }
          .status-default { background: #f1f5f9; color: #475569; }
          .schedule-row { display: flex; gap: 40px; flex-wrap: wrap; }
          .schedule-item { display: flex; align-items: center; gap: 12px; }
          .date-circle { width: 60px; height: 60px; border-radius: 50%; display: flex; flex-direction: column; align-items: center; justify-content: center; font-weight: bold; }
          .date-circle.start { border: 3px solid #22c55e; color: #22c55e; }
          .date-circle.end { border: 3px solid #ef4444; color: #ef4444; }
          .date-circle .day { font-size: 20px; }
          .date-circle .month { font-size: 10px; text-transform: uppercase; }
          .date-details { font-size: 13px; }
          .date-details .label { color: #64748b; font-size: 11px; text-transform: uppercase; }
          .date-details .date { font-weight: 600; color: #1e293b; }
          .date-details .time { color: #64748b; }
          table { width: 100%; border-collapse: collapse; margin-top: 16px; }
          th { background: #4f46e5; color: white; padding: 12px 16px; text-align: left; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; }
          td { padding: 12px 16px; border-bottom: 1px solid #e2e8f0; font-size: 13px; }
          tr:nth-child(even) { background: #f8fafc; }
          tr:hover { background: #f1f5f9; }
          .barcode-section { text-align: center; padding: 20px; background: white; border-radius: 8px; border: 1px solid #e2e8f0; }
          .barcode-section img { max-width: 250px; height: auto; }
          .barcode-id { font-family: monospace; font-size: 20px; font-weight: bold; margin-top: 12px; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; font-size: 11px; color: #64748b; text-align: center; }
          @media print {
            body { padding: 20px; }
            .header { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            th { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>🏭 ${t('pages.productionTasks.productionLotReportTitle')}</h1>
          <p>${t('pages.productionTasks.reportSubtitle')}</p>
          <div class="report-meta">
            <span>${t('pages.productionTasks.taskId')}: ${data.productionLotId}</span>
            <span>${t('pages.productionTasks.generatedAt', { date: new Date().toLocaleString() })}</span>
          </div>
        </div>

        <div class="section">
          <div class="section-title">${t('pages.productionTasks.productionSchedule')}</div>
          <div class="schedule-row">
            <div class="schedule-item">
              <div class="date-circle start">
                <span class="day">${data.productionStartDate ? new Date(data.productionStartDate).getDate() : '-'}</span>
                <span class="month">${data.productionStartDate ? new Date(data.productionStartDate).toLocaleString('default', { month: 'short' }) : ''}</span>
              </div>
              <div class="date-details">
                <div class="label">${t('pages.productionTasks.startDate')}</div>
                <div class="date">${data.productionStartDate ? new Date(data.productionStartDate).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</div>
                <div class="time">${data.productionStartDate ? new Date(data.productionStartDate).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''}</div>
              </div>
            </div>
            <div class="schedule-item">
              <div class="date-circle end">
                <span class="day">${data.productionEndDate ? new Date(data.productionEndDate).getDate() : '-'}</span>
                <span class="month">${data.productionEndDate ? new Date(data.productionEndDate).toLocaleString('default', { month: 'short' }) : ''}</span>
              </div>
              <div class="date-details">
                <div class="label">${t('pages.productionTasks.endDate')}</div>
                <div class="date">${data.productionEndDate ? new Date(data.productionEndDate).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</div>
                <div class="time">${data.productionEndDate ? new Date(data.productionEndDate).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">${t('pages.productionTasks.lotInformation')}</div>
          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">${t('pages.productionTasks.taskId')}</div>
              <div class="info-value" style="color: #4f46e5;">${data.productionLotId || '-'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">${t('pages.productionTasks.orderId')}</div>
              <div class="info-value" style="color: #4f46e5;">${data.productionOrderId || '-'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">${t('pages.productionTasks.site')}</div>
              <div class="info-value">${data.siteId || '-'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">${t('pages.productionTasks.status')}</div>
              <div class="info-value">
                <span class="status-badge ${data.lifeCycleStatusCode === '4' ? 'status-closed' : data.lifeCycleStatusCode === '3' ? 'status-progress' : 'status-default'}">
                  ${data.lifeCycleStatusDescription || t('commonUi.unknown')}
                </span>
              </div>
            </div>
            <div class="info-item">
              <div class="info-label">${t('pages.productionTasks.product')}</div>
              <div class="info-value">${data.mainOutputProductId || '-'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">${t('pages.productionTasks.totalQuantity')}</div>
              <div class="info-value">${data.totalQuantity ?? '-'} ${data.totalQuantityUnit || ''}</div>
            </div>
            <div class="info-item">
              <div class="info-label">${t('pages.productionTasks.completedLabel')}</div>
              <div class="info-value">${data.totalCompletedQuantity ?? '-'} ${data.totalCompletedQuantityUnit || ''}</div>
            </div>
          </div>
        </div>

        ${data.barcodeBase64 ? `
        <div class="section">
          <div class="section-title">${t('pages.productionTasks.barcode')}</div>
          <div class="barcode-section">
            <img src="data:image/png;base64,${data.barcodeBase64}" alt="${t('pages.productionTasks.barcode')}" />
            <div class="barcode-id">${data.productionLotId}</div>
          </div>
        </div>
        ` : ''}

        ${allActivities.length > 0 ? `
        <div class="section">
          <div class="section-title">${t('pages.productionTasks.activities')} (${allActivities.length})</div>
          <table>
            <thead>
              <tr>
                <th>${t('pages.productionTasks.operation')}</th>
                <th>${t('pages.productionTasks.activity')}</th>
                <th>${t('pages.productionTasks.description')}</th>
                <th>${t('pages.productionTasks.duration')}</th>
              </tr>
            </thead>
            <tbody>
              ${allActivities.map(activity => `
                <tr>
                  <td><strong>${activity.operationId || activity.operationActivityTypeCode || '-'}</strong></td>
                  <td>${activity.activityId || activity.categoryCode || '-'}</td>
                  <td>${activity.activityDescription || activity.operationId || '-'}</td>
                  <td>${activity.plannedDuration || '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
        ` : ''}

        <div class="footer">
          <p>${t('pages.productionTasks.reportFooter', { date: new Date().toLocaleString() })}</p>
          <p>${t('pages.productionTasks.reportTaskOrder', { taskId: data.productionLotId, orderId: data.productionOrderId || '-' })}</p>
        </div>
      </body>
      </html>
    `;
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-600 shadow-sm overflow-hidden mt-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 p-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-lg">
              <span className="text-sm text-gray-500 dark:text-gray-400">Show</span>
              <select
                value={pageSize}
                onChange={handlePageSizeChange}
                className="px-2 py-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none cursor-pointer hover:border-gray-300 dark:hover:border-gray-500 transition-colors dark:text-white"
              >
                <option value={8}>8</option>
                <option value={12}>12</option>
                <option value={16}>16</option>
                <option value={20}>20</option>
                <option value={24}>24</option>
                <option value={32}>32</option>
                <option value={48}>48</option>
              </select>
              <span className="text-sm text-gray-500 dark:text-gray-400">per page</span>
            </div>

            <div className="hidden lg:block text-sm text-gray-500 dark:text-gray-400">
              <span className="font-medium text-gray-700 dark:text-gray-300">{(currentPage - 1) * pageSize + 1}</span>
              <span> - </span>
              <span className="font-medium text-gray-700 dark:text-gray-300">
                {Math.min(currentPage * pageSize, totalItems)}
              </span>
              <span> of </span>
              <span className="font-medium text-gray-700 dark:text-gray-300">{totalItems}</span>
              <span> tasks</span>
            </div>
          </div>

          <div className="flex items-center justify-center lg:justify-end gap-1">
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className="hidden sm:flex items-center justify-center w-10 h-10 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white dark:disabled:hover:bg-gray-800 transition-all"
              title="First page"
            >
              <ChevronLeft className="w-4 h-4" />
              <ChevronLeft className="w-4 h-4 -ml-2" />
            </button>

            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="flex items-center justify-center w-10 h-10 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white dark:disabled:hover:bg-gray-800 transition-all"
              title="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <div className="hidden md:flex items-center gap-1 mx-1">
              {startPage > 1 && (
                <>
                  <button
                    onClick={() => handlePageChange(1)}
                    className="min-w-[40px] h-10 px-3 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    1
                  </button>
                  {startPage > 2 && (
                    <span className="px-2 text-gray-400">...</span>
                  )}
                </>
              )}

              {pages.map(page => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`min-w-[40px] h-10 px-3 rounded-lg text-sm font-medium transition-all ${currentPage === page
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-500/30 scale-105'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-transparent hover:border-gray-200'
                    }`}
                >
                  {page}
                </button>
              ))}

              {endPage < totalPages && (
                <>
                  {endPage < totalPages - 1 && (
                    <span className="px-2 text-gray-400">...</span>
                  )}
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    className="min-w-[40px] h-10 px-3 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    {totalPages}
                  </button>
                </>
              )}
            </div>

            <div className="flex md:hidden items-center gap-2 px-2">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Page <span className="font-bold text-blue-600 dark:text-blue-400">{currentPage}</span> of {totalPages}
              </span>
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="flex items-center justify-center w-10 h-10 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white dark:disabled:hover:bg-gray-800 transition-all"
              title="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>

            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="hidden sm:flex items-center justify-center w-10 h-10 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white dark:disabled:hover:bg-gray-800 transition-all"
              title="Last page"
            >
              <ChevronRight className="w-4 h-4" />
              <ChevronRight className="w-4 h-4 -ml-2" />
            </button>
          </div>

          <div className="flex lg:hidden items-center justify-center text-sm text-gray-500 dark:text-gray-400">
            <span className="font-medium text-gray-700 dark:text-gray-300">{(currentPage - 1) * pageSize + 1}</span>
            <span> - </span>
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {Math.min(currentPage * pageSize, totalItems)}
            </span>
            <span> of </span>
            <span className="font-medium text-gray-700 dark:text-gray-300">{totalItems}</span>
          </div>
        </div>

        <div className="h-1 w-full bg-gray-100 dark:bg-gray-700">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-300"
            style={{ width: `${(currentPage / totalPages) * 100}%` }}
          />
        </div>
      </div>
    );
  };

  if (loading && !hasLoadedOnce) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-6">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <StandardPageSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* SAP Sync Overlay */}
      {syncLoading && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-10 max-w-md w-full mx-4 flex flex-col items-center text-center border border-gray-200 dark:border-gray-700 animate-fade-in">
            <div className="relative w-24 h-24 mb-6">
              <div className="absolute inset-0 rounded-full border-4 border-emerald-200 dark:border-emerald-900 animate-ping opacity-30" />
              <div className="absolute inset-2 rounded-full border-4 border-teal-300 dark:border-teal-700 animate-spin" style={{ borderTopColor: 'transparent' }} />
              <div className="absolute inset-5 rounded-full border-4 border-emerald-400 dark:border-emerald-600 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s', borderBottomColor: 'transparent' }} />
              <div className="absolute inset-0 flex items-center justify-center">
                <Database className="w-8 h-8 text-emerald-600 dark:text-emerald-400 animate-pulse" />
              </div>
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t('pages.productionTasks.syncingFromSap')}</h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
              {t('pages.productionTasks.syncingDesc')}
            </p>
            <div className="mt-5 flex items-center gap-2 text-xs text-emerald-600 dark:text-emerald-400 font-medium">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              <span>{t('pages.productionTasks.syncingNote')}</span>
            </div>
            <button
              onClick={handleContinueInBackground}
              className="mt-6 px-6 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl text-sm font-semibold transition-all duration-200 hover:shadow-md"
            >
              {t('pages.productionTasks.continueBackground')}
            </button>
          </div>
        </div>
      )}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6 animate-fade-in">
        <PageFadeSection>
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('pages.productionTasks.pageTitle')}</h1>
                <p className="text-gray-500 dark:text-gray-400 mt-1">{t('pages.productionTasks.pageSubtitle')}</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSyncFromSap}
                  disabled={syncLoading || syncInBackground}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-xl font-medium transition-all shadow-lg shadow-emerald-500/30 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {(syncLoading || syncInBackground) ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Download className="w-5 h-5" />
                  )}
                  {syncLoading ? t('pages.productionTasks.syncingFromSap') : syncInBackground ? t('pages.productionTasks.syncingInBackground') : t('pages.productionTasks.syncFromSap')}
                </button>
                <button
                  onClick={openSapPreviewModal}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-xl font-medium transition-all shadow-lg shadow-indigo-500/30"
                >
                  <Eye className="w-5 h-5" />
                  {t('pages.productionTasks.sapPreview')}
                </button>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{t('pages.productionTasks.totalTasks')}</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">{totalItems}</p>
                </div>
                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                  <Package className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
              <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
                {t('pages.productionTasks.acrossPages', { pages: totalPages })}
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{t('pages.productionTasks.inProgress')}</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">
                    {statusCounts['in-progress'] ?? 0}
                  </p>
                </div>
                <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-xl">
                  <Clock className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                </div>
              </div>
              <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
                {t('pages.productionTasks.activeRuns')}
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{t('pages.productionTasks.completed')}</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">
                    {statusCounts['completed'] ?? 0}
                  </p>
                </div>
                <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-xl">
                  <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
              </div>
              <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
                {t('pages.productionTasks.finishedSuccessfully')}
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{t('pages.productionTasks.completionRate')}</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">
                    {totalItems > 0
                      ? Math.round(((statusCounts['completed'] ?? 0) / totalItems) * 100)
                      : 0}%
                  </p>
                </div>
                <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                  <BarChart3 className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
              </div>
              <div className="mt-4 w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                <div
                  className="bg-purple-600 h-2 rounded-full transition-all duration-500"
                  style={{
                    width: `${totalItems > 0
                      ? Math.round(((statusCounts['completed'] ?? 0) / totalItems) * 100)
                      : 0}%`
                  }}
                ></div>
              </div>
            </div>
          </div>
        </PageFadeSection>
        {/* SAP Production Task Lookup Section */}
        <div className="bg-gradient-to-r from-blue-50 dark:from-blue-900/20 to-indigo-50 dark:to-indigo-900/20 rounded-2xl p-6 shadow-sm border border-blue-100 dark:border-blue-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-600 rounded-xl">
              <ExternalLink className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('pages.productionTasks.taskLookup')}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('pages.productionTasks.taskLookupSubtitle')}</p>
            </div>
          </div>

          <div className="flex gap-3 mb-4">
            <div className="flex-1 relative">
              <input
                type="text"
                value={sapTaskId}
                onChange={(e) => setSapTaskId(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSapTaskLookup()}
                placeholder={t('pages.productionTasks.enterTaskId')}
                className="w-full px-4 py-3 border-2 border-blue-200 dark:border-blue-800 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none bg-white dark:bg-gray-700 dark:text-white"
                disabled={sapLoading}
              />
              {sapTaskId && (
                <button
                  onClick={clearSapLookup}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-full"
                >
                  <X className="w-4 h-4 text-gray-400" />
                </button>
              )}
            </div>
            <button
              onClick={handleSapTaskLookup}
              disabled={sapLoading || !sapTaskId.trim()}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-xl font-medium transition-all flex items-center gap-2"
            >
              {sapLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('pages.productionTasks.loading')}
                </>
              ) : (
                <>
                  <Search className="w-5 h-5" />
                  {t('pages.productionTasks.lookup')}
                </>
              )}
            </button>
          </div>

          {sapError && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-xl flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0" />
              <p className="text-red-700 dark:text-red-400">{sapError}</p>
            </div>
          )}

          {sapLotData && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-600 overflow-hidden">
              <div className="p-4 bg-gradient-to-r from-gray-50 dark:from-gray-700 to-gray-100 dark:to-gray-700 border-b border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {t('pages.productionTasks.productionLot')}: {sapLotData.productionLotId || sapLotData.id}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {sapLotData.mainOutputProductDescription || sapLotData.description || t('pages.productionTasks.noDescription')}
                    </p>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${sapLotData.lifeCycleStatusCode === '4' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                    : sapLotData.lifeCycleStatusCode === '3' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                      : sapLotData.lifeCycleStatusCode === '2' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}>
                    {sapLotData.lifeCycleStatusCode === '4' && <CheckCircle className="w-4 h-4" />}
                    {sapLotData.lifeCycleStatusCode === '3' && <Clock className="w-4 h-4" />}
                    {sapLotData.lifeCycleStatusDescription || `${t('pages.productionTasks.status')}: ${sapLotData.lifeCycleStatusCode || t('commonUi.unknown')}`}
                  </span>
                </div>
              </div>

              <div className="p-4 bg-gradient-to-r from-green-50 dark:from-green-900/20 to-red-50 dark:to-red-900/20 border-b border-gray-200 dark:border-gray-600">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  <h4 className="font-medium text-gray-900 dark:text-white">{t('pages.productionTasks.productionSchedule')}</h4>
                </div>
                <div className="flex flex-wrap gap-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full border-4 border-green-400 flex flex-col items-center justify-center bg-white dark:bg-gray-800">
                      <span className="text-lg font-bold text-green-600 dark:text-green-400">
                        {sapLotData.productionStartDate ? new Date(sapLotData.productionStartDate).getDate() : '-'}
                      </span>
                      <span className="text-[10px] text-green-600 dark:text-green-400">
                        {sapLotData.productionStartDate ? new Date(sapLotData.productionStartDate).toLocaleString('default', { month: 'short' }) : ''}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{t('pages.productionTasks.startDate')}</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {sapLotData.productionStartDate
                          ? new Date(sapLotData.productionStartDate).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })
                          : '-'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {sapLotData.productionStartDate
                          ? new Date(sapLotData.productionStartDate).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                          : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full border-4 border-red-400 flex flex-col items-center justify-center bg-white dark:bg-gray-800">
                      <span className="text-lg font-bold text-red-600 dark:text-red-400">
                        {sapLotData.productionEndDate ? new Date(sapLotData.productionEndDate).getDate() : '-'}
                      </span>
                      <span className="text-[10px] text-red-600 dark:text-red-400">
                        {sapLotData.productionEndDate ? new Date(sapLotData.productionEndDate).toLocaleString('default', { month: 'short' }) : ''}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{t('pages.productionTasks.endDate')}</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {sapLotData.productionEndDate
                          ? new Date(sapLotData.productionEndDate).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })
                          : '-'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {sapLotData.productionEndDate
                          ? new Date(sapLotData.productionEndDate).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                          : ''}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4 border-b border-gray-100 dark:border-gray-700">
                <div className="p-3 border border-gray-200 dark:border-gray-600 rounded-lg">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('pages.productionTasks.status')}</p>
                  <span className={`inline-flex items-center px-2 py-1 rounded text-sm font-medium ${sapLotData.lifeCycleStatusCode === '4' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                    : sapLotData.lifeCycleStatusCode === '3' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}>
                    {sapLotData.lifeCycleStatusDescription || t('commonUi.unknown')}
                  </span>
                </div>
                <div className="p-3 border border-gray-200 dark:border-gray-600 rounded-lg">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('pages.productionTasks.product')}</p>
                  <p className="font-semibold text-gray-900 dark:text-white">{sapLotData.mainOutputProductId || '-'}</p>
                </div>
                <div className="p-3 border border-gray-200 dark:border-gray-600 rounded-lg">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('pages.productionTasks.totalQty')}</p>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {sapLotData.totalQuantity ?? '-'} {sapLotData.totalQuantityUnit || ''}
                  </p>
                </div>
                <div className="p-3 border border-gray-200 dark:border-gray-600 rounded-lg">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('pages.productionTasks.completedLabel')}</p>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {sapLotData.totalCompletedQuantity ?? '-'} {sapLotData.totalCompletedQuantityUnit || ''}
                  </p>
                </div>
              </div>

              <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <Hash className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('pages.productionTasks.taskId')}</p>
                    <p className="font-medium text-blue-600 dark:text-blue-400">{sapLotData.productionLotId || '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <FileText className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('pages.productionTasks.orderId')}</p>
                    <p className="font-medium text-blue-600 dark:text-blue-400">{sapLotData.productionOrderId || '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <Factory className="w-4 h-4 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('pages.productionTasks.site')}</p>
                    <p className="font-medium text-gray-900 dark:text-white">{sapLotData.siteId || '-'}</p>
                    {sapLotData.siteDescription && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">{sapLotData.siteDescription}</p>
                    )}
                  </div>
                </div>
              </div>

              {(() => {
                const allActivities = sapLotData.confirmationGroups?.flatMap(group =>
                  group.activities?.map(activity => ({
                    ...activity,
                    confirmationGroupId: group.confirmationGroupId
                  })) || []
                ) || sapLotData.activities || [];

                if (allActivities.length > 0) {
                  return (
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Activity className="w-4 h-4 text-purple-500 dark:text-purple-400" />
                          <h4 className="font-medium text-gray-900 dark:text-white">{t('pages.productionTasks.activities')}</h4>
                        </div>
                        <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-full text-xs font-medium">
                          {t('pages.productionTasks.totalCount', { count: allActivities.length })}
                        </span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-purple-50 dark:bg-purple-900/20">
                            <tr>
                              <th className="text-left px-3 py-2 text-gray-700 dark:text-gray-300 font-semibold">{t('pages.productionTasks.operation')}</th>
                              <th className="text-left px-3 py-2 text-gray-700 dark:text-gray-300 font-semibold">{t('pages.productionTasks.activity')}</th>
                              <th className="text-left px-3 py-2 text-gray-700 dark:text-gray-300 font-semibold">{t('pages.productionTasks.description')}</th>
                              <th className="text-left px-3 py-2 text-gray-700 dark:text-gray-300 font-semibold">{t('pages.productionTasks.duration')}</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {allActivities.map((activity, index) => (
                              <tr key={activity.activityUUID || index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td className="px-3 py-2">
                                  <span className="text-purple-600 dark:text-purple-400 font-medium">
                                    {activity.operationId || activity.operationActivityTypeCode || '-'}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                                  {activity.activityId || activity.categoryCode || '-'}
                                </td>
                                <td className="px-3 py-2 text-gray-900 dark:text-white">
                                  {activity.activityDescription || activity.operationId || '-'}
                                </td>
                                <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                                  {formatDuration(activity.plannedDuration) || '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {(!sapLotData.confirmationGroups?.some(g => g.activities?.length > 0) &&
                (!sapLotData.activities || sapLotData.activities.length === 0)) && (
                  <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                    <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>{t('pages.productionTasks.noActivitiesForTask')}</p>
                  </div>
                )}
            </div>
          )}
        </div>

        {/* Modern Advanced Filters Section */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          {/* Filter Header */}
          <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-800 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="flex items-center justify-between w-full group"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-md">
                  <SlidersHorizontal className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('pages.productionTasks.advancedFilters')}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('pages.productionTasks.filterSubtitle')}</p>
                </div>
                {activeFilterCount > 0 && (
                  <span className="ml-2 px-2.5 py-0.5 text-xs font-bold rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-sm">
                    {activeFilterCount}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {activeFilterCount > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      clearAllFilters();
                    }}
                    className="px-3 py-1.5 text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-medium hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all"
                  >
                    {t('pages.productionTasks.clearFilters')}
                  </button>
                )}
                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${showAdvancedFilters ? 'rotate-180' : ''}`} />
              </div>
            </button>
          </div>

          {/* Filter Content */}
          {showAdvancedFilters && (
            <div className="p-6 bg-gray-50 dark:bg-gray-800/50 animate-slideDown">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                {/* Production Lot ID Filter */}
                <div className="group">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                    <div className="p-1 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                      <Boxes className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
                    </div>
                    {t('pages.productionTasks.productionLotId')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Package className="h-4 w-4 text-gray-400 group-focus-within:text-purple-500 transition-colors" />
                    </div>
                    <input
                      type="text"
                      value={localFilterProductionLotId}
                      onChange={(e) => setLocalFilterProductionLotId(e.target.value)}
                      placeholder={t('pages.productionTasks.productionLotIdPlaceholder')}
                      disabled={loading}
                      className="w-full pl-10 pr-10 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition-all outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    {localFilterProductionLotId && (
                      <button
                        onClick={() => setLocalFilterProductionLotId('')}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        <X className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" />
                      </button>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('pages.productionTasks.filterByLot')}</p>
                </div>

                {/* Production Task ID Filter */}
                <div className="group">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                    <div className="p-1 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                      <Hash className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                    </div>
                    {t('pages.productionTasks.orderId')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Clipboard className="h-4 w-4 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                    </div>
                    <input
                      type="text"
                      value={localFilterProductionTaskId}
                      onChange={(e) => setLocalFilterProductionTaskId(e.target.value)}
                      placeholder={t('pages.productionTasks.orderIdPlaceholder')}
                      disabled={loading}
                      className="w-full pl-10 pr-10 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    {localFilterProductionTaskId && (
                      <button
                        onClick={() => setLocalFilterProductionTaskId('')}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        <X className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" />
                      </button>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('pages.productionTasks.filterByOrder')}</p>
                </div>

                {/* Sales Order No Filter */}
                <div className="group">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                    <div className="p-1 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                      <FileText className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    {t('pages.productionTasks.salesOrderNo')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <ShoppingCart className="h-4 w-4 text-gray-400 group-focus-within:text-emerald-500 transition-colors" />
                    </div>
                    <input
                      type="text"
                      value={localFilterSalesOrderNo}
                      onChange={(e) => setLocalFilterSalesOrderNo(e.target.value)}
                      placeholder={t('pages.productionTasks.salesOrderPlaceholder')}
                      disabled={loading}
                      className="w-full pl-10 pr-10 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    {localFilterSalesOrderNo && (
                      <button
                        onClick={() => setLocalFilterSalesOrderNo('')}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        <X className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" />
                      </button>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('pages.productionTasks.filterBySales')}</p>
                </div>

                {/* Status Filter Dropdown */}
                <div className="group">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                    <div className="p-1 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                      <Activity className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                    </div>
                    {t('pages.productionTasks.status')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Filter className="h-4 w-4 text-gray-400 group-focus-within:text-amber-500 transition-colors" />
                    </div>
                    <select
                      value={filterStatus}
                      onChange={(e) => {
                        setFilterStatus(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-all outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white cursor-pointer appearance-none"
                    >
                      <option value="">{t('pages.productionTasks.all')}</option>
                      <option value="1">{t('pages.productionTasks.notStarted')}</option>
                      <option value="2">{t('pages.productionTasks.inProgress')}</option>
                      <option value="3">{t('pages.productionTasks.completed')}</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('pages.productionTasks.status')}</p>
                </div>
              </div>

              {/* Active Filters Display */}
              {activeFilterCount > 0 && (
                <div className="mt-5 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">{t('pages.productionTasks.activeFilters')}</span>
                    {filterProductionTaskId && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg text-sm">
                        <Clipboard className="w-3.5 h-3.5" />
                        Task: {filterProductionTaskId}
                        <button
                          onClick={() => setLocalFilterProductionTaskId('')}
                          className="ml-1 hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    )}
                    {filterProductionLotId && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg text-sm">
                        <Package className="w-3.5 h-3.5" />
                        Lot: {filterProductionLotId}
                        <button
                          onClick={() => setLocalFilterProductionLotId('')}
                          className="ml-1 hover:bg-purple-200 dark:hover:bg-purple-800 rounded-full p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    )}
                    {filterSalesOrderNo && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-lg text-sm">
                        <FileText className="w-3.5 h-3.5" />
                        SO: {filterSalesOrderNo}
                        <button
                          onClick={() => setLocalFilterSalesOrderNo('')}
                          className="ml-1 hover:bg-emerald-200 dark:hover:bg-emerald-800 rounded-full p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    )}
                    {filterStatus && filterStatus !== '' && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-lg text-sm">
                        <Activity className="w-3.5 h-3.5" />
                        {t('pages.productionTasks.status')}: {
                          filterStatus === '1' ? t('pages.productionTasks.notStarted') :
                            filterStatus === '2' ? t('pages.productionTasks.inProgress') :
                              filterStatus === '3' ? t('pages.productionTasks.completed') : filterStatus
                        }
                        <button
                          onClick={() => setFilterStatus('')}
                          className="ml-1 hover:bg-amber-200 dark:hover:bg-amber-800 rounded-full p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Tasks Display - Card View */}
        {viewMode === 'card' && (
          <div className="relative min-h-[200px]">
            {loading && hasLoadedOnce ? (
              <div
                className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-2xl bg-white/70 dark:bg-gray-900/65 backdrop-blur-[2px] pointer-events-none"
                aria-busy="true"
                aria-live="polite"
              >
                <Loader2 className="w-9 h-9 text-blue-600 dark:text-blue-400 animate-spin" />
                <p className="text-sm font-medium text-gray-700 dark:text-gray-200">{t('pages.productionTasks.updatingTaskList')}</p>
              </div>
            ) : null}
            {tasks.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-12 text-center border border-gray-200 dark:border-gray-600">
                <div className="w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Package className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{t('pages.productionTasks.noTasksFound')}</h3>
                <p className="text-gray-500 dark:text-gray-400 mb-6">
                  {localSearchTerm || filterProductionTaskId || filterProductionLotId || filterSalesOrderNo || filterStatus
                    ? t('pages.productionTasks.adjustSearch')
                    : t('pages.productionTasks.noTasksAvailable')}
                </p>
                {(localSearchTerm || filterProductionTaskId || filterProductionLotId || filterSalesOrderNo || filterStatus) ? (
                  <button
                    onClick={clearAllFilters}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white rounded-xl font-medium transition-all shadow-lg"
                  >
                    <RefreshCw className="w-5 h-5" />
                    {t('pages.productionTasks.clearAllFilters')}
                  </button>
                ) : (
                  <button
                    onClick={openSapPreviewModal}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-xl font-medium transition-all shadow-lg"
                  >
                    <Eye className="w-5 h-5" />
                    {t('pages.productionTasks.importFromSap')}
                  </button>
                )}
              </div>
            ) : (
              <div className="flex flex-col min-h-[600px]">
                <div className="flex-1">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {tasks.map((task) => (
                      <div key={task.id || task.productionLotId} className="h-full">
                        <ProductionTaskCard
                          task={task}
                          onClick={() => openTaskDetailModal(task)}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {renderPagination()}
              </div>
            )}
          </div>
        )}

        {/* Task Detail Modal */}
        {isTaskDetailModalOpen && (
          <ProductionTaskDetailModal
            isOpen={isTaskDetailModalOpen}
            onClose={closeTaskDetailModal}
            task={detailedTask}
            loading={detailModalLoading}
            mode="view"
            cachedUsers={cachedUsers}
            cachedTeams={cachedTeams}
            onSave={async (updatedData) => {
              try {
                await fetchTasks();
              } catch (error) {
                console.error('Failed to update task assignment:', error);
                throw error;
              }
            }}
            onAssign={(task) => {
              setAssignTaskData(task);
              setIsAssignModalOpen(true);
            }}
          />
        )}

        {/* SAP Preview Modal */}
        {isSapPreviewOpen && (
          <div className="fixed inset-0 bg-gradient-to-br from-black/70 via-black/60 to-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl max-w-6xl w-full max-h-[95vh] overflow-hidden transform transition-all animate-in zoom-in-95 duration-200">
              <div className="relative bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 p-6">
                <div className="relative z-10 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm">
                      <FileText className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white">{t('pages.productionTasks.sapProductionTaskPreview')}</h2>
                      <p className="text-indigo-100 text-sm mt-1">
                        {t('pages.productionTasks.lookupBy', { mode: lookupMode === 'taskId' ? t('pages.productionTasks.taskIdMode') : lookupMode === 'orderId' ? t('pages.productionTasks.orderIdMode') : t('pages.productionTasks.salesOrderMode') })}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={closeSapPreviewModal}
                    className="p-2.5 hover:bg-white/20 rounded-xl transition-all duration-200 group"
                  >
                    <X className="w-6 h-6 text-white group-hover:rotate-90 transition-transform duration-200" />
                  </button>
                </div>
                <div className="pointer-events-none absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
                <div className="pointer-events-none absolute bottom-0 left-0 w-32 h-32 bg-purple-400/20 rounded-full blur-2xl"></div>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(95vh-180px)]">
                <div className="mb-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        {lookupMode === 'taskId' ? (
                          <>
                            <Clipboard className="w-4 h-4 inline mr-2" />
                            {t('pages.productionTasks.enterProductionTaskId')}
                          </>
                        ) : lookupMode === 'orderId' ? (
                          <>
                            <FileText className="w-4 h-4 inline mr-2" />
                            {t('pages.productionTasks.enterProductionOrderId')}
                          </>
                        ) : (
                          <>
                            <Hash className="w-4 h-4 inline mr-2" />
                            {t('pages.productionTasks.enterSalesOrderNo')}
                          </>
                        )}
                      </label>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {lookupMode === 'taskId'
                          ? t('pages.productionTasks.lookupSpecific')
                          : lookupMode === 'orderId'
                            ? t('pages.productionTasks.findProduction')
                            : t('pages.productionTasks.searchSalesOrder')}
                      </p>
                    </div>

                    <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-700 p-1.5 rounded-xl border border-gray-200 dark:border-gray-600">
                      <button
                        className={`text-sm font-medium px-3 py-1.5 rounded-lg transition-all ${lookupMode === 'taskId'
                          ? 'bg-indigo-600 text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                        onClick={() => { setLookupMode('taskId'); setSpecResults(null); setSpecSelectedOrder(null); }}
                      >
                        <Clipboard className="w-3 h-3 inline mr-1.5" />
                        {t('pages.productionTasks.taskIdMode')}
                      </button>
                      <button
                        className={`text-sm font-medium px-3 py-1.5 rounded-lg transition-all ${lookupMode === 'orderId'
                          ? 'bg-purple-600 text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                        onClick={() => { setLookupMode('orderId'); setSpecResults(null); setSpecSelectedOrder(null); }}
                      >
                        <FileText className="w-3 h-3 inline mr-1.5" />
                        {t('pages.productionTasks.orderIdMode')}
                      </button>
                      <button
                        className={`text-sm font-medium px-3 py-1.5 rounded-lg transition-all ${lookupMode === 'salesOrder'
                          ? 'bg-emerald-600 text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                        onClick={() => { setLookupMode('salesOrder'); setSpecResults(null); setSpecSelectedOrder(null); }}
                      >
                        <Hash className="w-3 h-3 inline mr-1.5" />
                        {t('pages.productionTasks.salesOrderMode')}
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1 relative">
                      <div className="flex items-center">
                        {lookupMode === 'taskId' ? (
                          <div className="absolute left-3 p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                            <Clipboard className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                          </div>
                        ) : lookupMode === 'orderId' ? (
                          <div className="absolute left-3 p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                            <FileText className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                          </div>
                        ) : (
                          <div className="absolute left-3 p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                            <Hash className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                          </div>
                        )}
                        <input
                          type="text"
                          value={sapPreviewId}
                          onChange={(e) => setSapPreviewId(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleSapPreviewLookup()}
                          placeholder={
                            lookupMode === 'taskId'
                              ? t('pages.productionTasks.enterTaskId')
                              : lookupMode === 'orderId'
                                ? t('pages.productionTasks.enterOrderId')
                                : t('pages.productionTasks.enterSalesOrderNo')
                          }
                          className={`w-full pl-14 pr-12 py-4 text-lg border-2 rounded-xl focus:ring-2 focus:ring-offset-2 transition-all outline-none dark:bg-gray-700 dark:text-white dark:focus:ring-offset-gray-800 ${lookupMode === 'taskId'
                            ? 'border-indigo-200 dark:border-indigo-700 focus:border-indigo-500 focus:ring-indigo-500'
                            : lookupMode === 'orderId'
                              ? 'border-purple-200 dark:border-purple-700 focus:border-purple-500 focus:ring-purple-500'
                              : 'border-emerald-200 dark:border-emerald-700 focus:border-emerald-500 focus:ring-emerald-500'
                            }`}
                          disabled={sapPreviewLoading}
                        />
                        {sapPreviewId && !sapPreviewLoading && (
                          <button
                            onClick={() => { setSapPreviewId(''); setSapPreviewData(null); setSapPreviewError(null); setSpecResults(null); setSpecSelectedOrder(null); }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-full"
                          >
                            <X className="w-5 h-5 text-gray-400" />
                          </button>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={handleSapPreviewLookup}
                      disabled={sapPreviewLoading || !sapPreviewId.trim()}
                      className={`px-8 py-4 text-white rounded-xl font-semibold transition-all flex items-center justify-center gap-2 shadow-lg min-w-[140px] ${lookupMode === 'taskId'
                        ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 disabled:from-gray-300 disabled:to-gray-400'
                        : lookupMode === 'orderId'
                          ? 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 disabled:from-gray-300 disabled:to-gray-400'
                          : 'bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 disabled:from-gray-300 disabled:to-gray-400'
                        }`}
                    >
                      {sapPreviewLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          {t('pages.productionTasks.fetching')}
                        </>
                      ) : (
                        <>
                          <Search className="w-5 h-5" />
                          {t('pages.productionTasks.fetch')}
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {sapPreviewError && (
                  <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-700 rounded-xl flex items-center gap-3">
                    <AlertTriangle className="w-6 h-6 text-red-500 dark:text-red-400 flex-shrink-0" />
                    <p className="text-red-700 dark:text-red-400 font-medium">{sapPreviewError}</p>
                  </div>
                )}

                {specResults && specResults.length > 0 && !sapPreviewData && (
                  <div className="mb-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <Layers className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                        {t('pages.productionTasks.lotsFound', { count: specResults.length, plural: specResults.length !== 1 ? 's' : '' })}
                      </h3>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {t('pages.productionTasks.salesOrderNo')}: <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400">{sapPreviewId}</span>
                        <span className="ml-2 px-2 py-0.5 text-xs rounded-full font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                          {t('pages.productionTasks.salesOrderNo')}
                        </span>
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t('pages.productionTasks.selectProdOrder')}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {specResults.map((order, idx) => {
                        const statusColors = {
                          '1': 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200',
                          '2': 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-blue-200',
                          '3': 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 border-yellow-200',
                          '4': 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-200',
                          '5': 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 border-purple-200',
                        };
                        const statusClass = statusColors[order.statusCode] || statusColors['1'];
                        const formatDate = (d) => {
                          if (!d) return '—';
                          try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; }
                        };

                        return (
                          <button
                            key={order.productionOrderId || idx}
                            onClick={() => handleSpecCardSelect(order)}
                            disabled={sapPreviewLoading}
                            className="text-left bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-600 p-5 hover:border-emerald-400 dark:hover:border-emerald-500 hover:shadow-lg transition-all duration-200 group disabled:opacity-50"
                          >
                            <div className="flex items-center justify-between mb-3">
                              <span className="font-mono font-bold text-indigo-600 dark:text-indigo-400 text-lg">
                                {order.productionOrderId || '—'}
                              </span>
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${statusClass}`}>
                                {order.statusText || getStatusDescription(order.statusCode) || t('commonUi.unknown')}
                              </span>
                            </div>

                            {order.productionLotId && (
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-xs text-gray-500 dark:text-gray-400">{t('commonUi.lot')}:</span>
                                <span className="text-sm font-medium text-gray-900 dark:text-white font-mono">{order.productionLotId}</span>
                              </div>
                            )}

                            <div className="flex items-center gap-2 mb-3">
                              <Package className="w-3.5 h-3.5 text-gray-400" />
                              <span className="text-sm text-gray-700 dark:text-gray-300 truncate" title={order.productDescription}>
                                {order.productDescription || '—'}
                              </span>
                            </div>

                            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700 pt-3 mt-1">
                              <div className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                <span>{formatDate(order.startDate)}</span>
                              </div>
                              {order.plannedQuantity != null && (
                                <span className="font-medium text-gray-700 dark:text-gray-300">
                                  {t('pages.productionTasks.quantity')}: {order.plannedQuantity} {order.plannedQuantityUnit || ''}
                                </span>
                              )}
                            </div>

                            <div className="mt-3 text-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                              <span className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold flex items-center justify-center gap-1">
                                <Eye className="w-3 h-3" />
                                {t('pages.productionTasks.viewProdOrderDetails')}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {sapPreviewData && (
                  <div className="space-y-6">
                    <div className="bg-gradient-to-br from-gray-50 dark:from-gray-700 to-gray-100 dark:to-gray-700 rounded-2xl border-2 border-gray-200 dark:border-gray-600 overflow-hidden">
                      <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-6 py-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Factory className="w-6 h-6 text-white" />
                          <span className="text-white font-bold text-lg">{t('pages.productionTasks.productionLotReportTitle')}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={handlePrintReport}
                            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                            title={t('pages.productionTasks.printReportTitle')}
                          >
                            <Printer className="w-5 h-5 text-white" />
                          </button>
                          <button
                            onClick={handleDownloadReport}
                            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                            title={t('pages.productionTasks.downloadReportTitle')}
                          >
                            <Download className="w-5 h-5 text-white" />
                          </button>
                        </div>
                      </div>

                      <div className="p-6">
                        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                          <div className="lg:col-span-1 space-y-4">
                            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border-2 border-gray-200 dark:border-gray-600 text-center shadow-sm">
                              <div className="mb-3">
                                <QrCode className="w-6 h-6 text-indigo-600 dark:text-indigo-400 mx-auto mb-2" />
                                <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">{t('pages.productionTasks.barcode')}</span>
                              </div>
                              {sapPreviewData.barcodeBase64 ? (
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700">
                                  <img
                                    src={`data:image/png;base64,${sapPreviewData.barcodeBase64}`}
                                    alt="Production Lot Barcode"
                                    className="mx-auto max-w-full h-auto"
                                  />
                                </div>
                              ) : (
                                <div className="bg-gray-100 dark:bg-gray-700 p-8 rounded-lg">
                                  <QrCode className="w-16 h-16 text-gray-300 mx-auto" />
                                  <p className="text-sm text-gray-400 mt-2">{t('pages.productionTasks.noBarcode')}</p>
                                </div>
                              )}
                              <p className="mt-3 font-mono text-lg font-bold text-gray-900 dark:text-white">
                                {sapPreviewData.productionLotId}
                              </p>
                            </div>

                            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border-2 border-gray-200 dark:border-gray-600 space-y-3 shadow-sm">
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-500 dark:text-gray-400">{t('pages.productionTasks.taskId')}</span>
                                <span className="font-mono font-bold text-indigo-600 dark:text-indigo-400">{sapPreviewData.productionLotId}</span>
                              </div>
                              <div className="border-t border-gray-100 dark:border-gray-700"></div>
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-500 dark:text-gray-400">{t('pages.productionTasks.orderId')}</span>
                                <span className="font-mono font-bold text-gray-900 dark:text-white">{sapPreviewData.productionOrderId || '-'}</span>
                              </div>
                              {sapPreviewData.salesOrderNo && (
                                <>
                                  <div className="border-t border-gray-100 dark:border-gray-700"></div>
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-500 dark:text-gray-400">{t('pages.productionTasks.salesOrder')}</span>
                                    <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400">{sapPreviewData.salesOrderNo}</span>
                                  </div>
                                </>
                              )}
                              <div className="border-t border-gray-100 dark:border-gray-700"></div>
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-500 dark:text-gray-400">{t('pages.productionTasks.site')}</span>
                                <span className="font-medium text-gray-900 dark:text-white">{sapPreviewData.siteId || '-'}</span>
                              </div>
                              {sapPreviewData.siteDescription && (
                                <>
                                  <div className="border-t border-gray-100 dark:border-gray-700"></div>
                                  <div>
                                    <span className="text-xs text-gray-400">{t('pages.productionTasks.siteName')}</span>
                                    <p className="font-medium text-gray-700 dark:text-gray-300 text-sm">{sapPreviewData.siteDescription}</p>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>

                          <div className="lg:col-span-3 space-y-4">
                            <div className="bg-gradient-to-r from-blue-50 dark:from-blue-900/20 to-indigo-50 dark:to-indigo-900/20 rounded-xl p-5 border-2 border-blue-200 dark:border-blue-800 shadow-sm">
                              <h4 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                {t('pages.productionTasks.productionSchedule')}
                              </h4>
                              <div className="grid grid-cols-2 gap-6">
                                <div className="relative">
                                  <div className="flex items-center gap-4">
                                    <div className="relative">
                                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center shadow-lg">
                                        <div className="w-16 h-16 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center">
                                          <div className="text-center">
                                            <span className="text-2xl font-bold text-green-600 dark:text-green-400">
                                              {sapPreviewData.productionStartDate
                                                ? new Date(sapPreviewData.productionStartDate).getDate()
                                                : '--'}
                                            </span>
                                            <p className="text-xs text-green-600 dark:text-green-400 font-medium">
                                              {sapPreviewData.productionStartDate
                                                ? new Date(sapPreviewData.productionStartDate).toLocaleString('default', { month: 'short' })
                                                : '---'}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                                    </div>
                                    <div>
                                      <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-semibold">{t('pages.productionTasks.startDate')}</span>
                                      <p className="text-lg font-bold text-gray-900 dark:text-white">
                                        {sapPreviewData.productionStartDate
                                          ? new Date(sapPreviewData.productionStartDate).toLocaleDateString('en-GB', {
                                            weekday: 'short',
                                            day: '2-digit',
                                            month: 'short',
                                            year: 'numeric'
                                          })
                                          : t('pages.productionTasks.notSet')}
                                      </p>
                                      <p className="text-sm text-gray-500 dark:text-gray-400">
                                        {sapPreviewData.productionStartDate
                                          ? new Date(sapPreviewData.productionStartDate).toLocaleTimeString('en-GB', {
                                            hour: '2-digit',
                                            minute: '2-digit'
                                          })
                                          : '--:--'}
                                      </p>
                                    </div>
                                  </div>
                                </div>

                                <div className="relative">
                                  <div className="flex items-center gap-4">
                                    <div className="relative">
                                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-red-400 to-rose-500 flex items-center justify-center shadow-lg">
                                        <div className="w-16 h-16 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center">
                                          <div className="text-center">
                                            <span className="text-2xl font-bold text-red-600 dark:text-red-400">
                                              {sapPreviewData.productionEndDate
                                                ? new Date(sapPreviewData.productionEndDate).getDate()
                                                : '--'}
                                            </span>
                                            <p className="text-xs text-red-600 dark:text-red-400 font-medium">
                                              {sapPreviewData.productionEndDate
                                                ? new Date(sapPreviewData.productionEndDate).toLocaleString('default', { month: 'short' })
                                                : '---'}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white"></div>
                                    </div>
                                    <div>
                                      <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-semibold">{t('pages.productionTasks.endDate')}</span>
                                      <p className="text-lg font-bold text-gray-900 dark:text-white">
                                        {sapPreviewData.productionEndDate
                                          ? new Date(sapPreviewData.productionEndDate).toLocaleDateString('en-GB', {
                                            weekday: 'short',
                                            day: '2-digit',
                                            month: 'short',
                                            year: 'numeric'
                                          })
                                          : t('pages.productionTasks.notSet')}
                                      </p>
                                      <p className="text-sm text-gray-500 dark:text-gray-400">
                                        {sapPreviewData.productionEndDate
                                          ? new Date(sapPreviewData.productionEndDate).toLocaleTimeString('en-GB', {
                                            hour: '2-digit',
                                            minute: '2-digit'
                                          })
                                          : '--:--'}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border-2 border-gray-200 dark:border-gray-600 shadow-sm">
                                <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{t('pages.productionTasks.status')}</span>
                                <div className="mt-2">
                                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold ${getStatusColorClass(sapPreviewData.lifeCycleStatusCode)}`}>
                                    {sapPreviewData.lifeCycleStatusCode === '4' && <CheckCircle className="w-4 h-4" />}
                                    {sapPreviewData.lifeCycleStatusCode === '3' && <Clock className="w-4 h-4" />}
                                    {sapPreviewData.lifeCycleStatusDescription || getStatusDescription(sapPreviewData.lifeCycleStatusCode)}
                                  </span>
                                </div>
                              </div>

                              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border-2 border-gray-200 dark:border-gray-600 shadow-sm">
                                <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{t('pages.productionTasks.product')}</span>
                                <p className="mt-2 font-semibold text-gray-900 dark:text-white truncate" title={sapPreviewData.mainOutputProductId}>
                                  {sapPreviewData.mainOutputProductId || '-'}
                                </p>
                                {sapPreviewData.mainOutputProductDescription && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate" title={sapPreviewData.mainOutputProductDescription}>
                                    {sapPreviewData.mainOutputProductDescription}
                                  </p>
                                )}
                              </div>

                              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border-2 border-gray-200 dark:border-gray-600 shadow-sm">
                                <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{t('pages.productionTasks.totalQty')}</span>
                                <p className="mt-2 font-semibold text-gray-900 dark:text-white">
                                  {sapPreviewData.totalQuantity ?? '-'} {sapPreviewData.totalQuantityUnit || ''}
                                </p>
                              </div>

                              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border-2 border-gray-200 dark:border-gray-600 shadow-sm">
                                <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{t('pages.productionTasks.completedLabel')}</span>
                                <p className="mt-2 font-semibold text-green-600 dark:text-green-400">
                                  {sapPreviewData.totalCompletedQuantity ?? '-'} {sapPreviewData.totalCompletedQuantityUnit || ''}
                                </p>
                              </div>
                            </div>

                            {sapPreviewData.confirmationGroups && sapPreviewData.confirmationGroups.length > 0 &&
                              sapPreviewData.confirmationGroups.reduce((acc, cg) => acc + (cg.activities?.length || 0), 0) > 0 && (
                                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-600 overflow-hidden shadow-sm">
                                  <div className="px-4 py-2.5 bg-gradient-to-r from-violet-500 to-purple-600 flex items-center justify-between">
                                    <h4 className="font-semibold text-white flex items-center gap-2 text-sm">
                                      <Activity className="w-4 h-4" />
                                      {t('pages.productionTasks.activities')}
                                    </h4>
                                    <span className="text-xs text-white/80 bg-white/20 px-2 py-0.5 rounded-full font-medium">
                                      {sapPreviewData.confirmationGroups.reduce((acc, cg) => acc + (cg.activities?.length || 0), 0)} total
                                    </span>
                                  </div>
                                  <div className="overflow-x-auto max-h-64 overflow-y-auto">
                                    <table className="w-full text-xs">
                                      <thead className="bg-gray-50/80 dark:bg-gray-700/80 sticky top-0 backdrop-blur-sm">
                                        <tr className="border-b border-gray-200 dark:border-gray-600">
                                          <th className="text-left px-3 py-2 text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wider">{t('pages.productionTasks.operation')}</th>
                                          <th className="text-left px-3 py-2 text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wider">{t('pages.productionTasks.activity')}</th>
                                          <th className="text-left px-3 py-2 text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wider min-w-[180px]">{t('pages.productionTasks.description')}</th>
                                          <th className="text-left px-3 py-2 text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wider">{t('pages.productionTasks.duration')}</th>
                                          <th className="text-left px-3 py-2 text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wider">{t('pages.productionTasks.status')}</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                        {sapPreviewData.confirmationGroups.flatMap((cg, cgIndex) =>
                                          (cg.activities || []).map((activity, actIndex) => (
                                            <tr key={`${cgIndex}-${actIndex}`} className="hover:bg-violet-50/50 transition-colors group">
                                              <td className="px-3 py-2.5">
                                                <span className="font-medium text-violet-600 dark:text-violet-400 group-hover:text-violet-700">
                                                  {activity.operationId || '-'}
                                                </span>
                                              </td>
                                              <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400 font-mono">{activity.activityId || '-'}</td>
                                              <td className="px-3 py-2.5">
                                                <span className="text-gray-800 dark:text-gray-200 font-medium block truncate max-w-[180px]" title={activity.activityDescription}>
                                                  {activity.activityDescription || '-'}
                                                </span>
                                              </td>
                                              <td className="px-3 py-2.5">
                                                <div className="flex flex-col gap-0.5">
                                                  <span className="text-gray-700 dark:text-gray-300 font-medium">{formatDuration(activity.plannedDuration) || '-'}</span>
                                                  {activity.openDuration && activity.openDuration !== 'PT0S' && (
                                                    <span className="text-[10px] text-orange-500 dark:text-orange-400 font-medium">Open: {formatDuration(activity.openDuration)}</span>
                                                  )}
                                                </div>
                                              </td>
                                              <td className="px-3 py-2.5">
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${activity.confirmationCompletedIndicator
                                                  ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 ring-1 ring-emerald-200 dark:ring-emerald-800'
                                                  : activity.lifeCycleStatusCode === '3'
                                                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 ring-1 ring-blue-200 dark:ring-blue-800'
                                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 ring-1 ring-gray-200 dark:ring-gray-600'
                                                  }`}>
                                                  {activity.confirmationCompletedIndicator ? (`✓ ` + t('pages.productionTasks.done')) :
                                                    activity.lifeCycleStatusCode === '3' ? (`● ` + t('pages.productionTasks.active')) :
                                                      activity.lifeCycleStatusCode || t('pages.productionTasks.pending')}
                                                </span>
                                              </td>
                                            </tr>
                                          ))
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}

                            {(!sapPreviewData.confirmationGroups ||
                              sapPreviewData.confirmationGroups.length === 0 ||
                              sapPreviewData.confirmationGroups.reduce((acc, cg) => acc + (cg.activities?.length || 0), 0) === 0) && (
                                <div className="bg-gradient-to-br from-violet-50 dark:from-violet-900/20 to-purple-50 dark:to-purple-900/20 rounded-xl p-4 border border-violet-200 dark:border-violet-700 flex items-center gap-3">
                                  <div className="w-10 h-10 bg-violet-100 dark:bg-violet-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <Activity className="w-5 h-5 text-violet-400" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-violet-700 dark:text-violet-400">{t('pages.productionTasks.noActivitiesTitle')}</p>
                                    <p className="text-xs text-violet-500">{t('pages.productionTasks.noActivities')}</p>
                                  </div>
                                </div>
                              )}

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                              {sapPreviewData.confirmationGroups &&
                                sapPreviewData.confirmationGroups.some(cg => cg.materialInputs?.length > 0) && (
                                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-600 overflow-hidden shadow-sm">
                                    <div className="px-3 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-between">
                                      <h4 className="font-semibold text-white flex items-center gap-2 text-sm">
                                        <Boxes className="w-4 h-4" />
                                        {t('pages.productionTasks.materialInputs')}
                                      </h4>
                                      <span className="text-xs text-white bg-white/20 w-5 h-5 rounded-full flex items-center justify-center font-bold">
                                        {sapPreviewData.confirmationGroups.reduce((acc, cg) => acc + (cg.materialInputs?.length || 0), 0)}
                                      </span>
                                    </div>
                                    <div className="overflow-x-auto max-h-48 overflow-y-auto">
                                      <table className="w-full text-xs">
                                        <thead className="bg-blue-50 dark:bg-blue-950 sticky top-0">
                                          <tr className="border-b border-blue-100 dark:border-blue-800">
                                            <th className="text-left px-2.5 py-1.5 text-blue-600 dark:text-blue-400 font-semibold uppercase tracking-wider text-[10px]">{t('pages.productionTasks.product')}</th>
                                            <th className="text-left px-2.5 py-1.5 text-blue-600 dark:text-blue-400 font-semibold uppercase tracking-wider text-[10px]">{t('pages.productionTasks.description')}</th>
                                            <th className="text-right px-2.5 py-1.5 text-blue-600 dark:text-blue-400 font-semibold uppercase tracking-wider text-[10px]">{t('pages.productionTasks.planned')}</th>
                                            <th className="text-right px-2.5 py-1.5 text-blue-600 dark:text-blue-400 font-semibold uppercase tracking-wider text-[10px]">{t('pages.productionTasks.confirmed')}</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50">
                                          {sapPreviewData.confirmationGroups.flatMap((cg, cgIndex) =>
                                            (cg.materialInputs || []).map((mi, miIndex) => (
                                              <tr key={`mi-${cgIndex}-${miIndex}`} className="hover:bg-blue-50/30 dark:hover:bg-blue-900/20 transition-colors">
                                                <td className="px-2.5 py-2">
                                                  <span className="font-bold text-blue-600 dark:text-blue-400 font-mono">
                                                    {mi.productId || '-'}
                                                  </span>
                                                </td>
                                                <td className="px-2.5 py-2 text-gray-600 dark:text-gray-400 max-w-[100px]">
                                                  <span className="block truncate" title={mi.productDescription}>
                                                    {mi.productDescription || '-'}
                                                  </span>
                                                </td>
                                                <td className="px-2.5 py-2 text-right">
                                                  <span className="font-semibold text-gray-700 dark:text-gray-300">{mi.plannedQuantity ?? '-'}</span>
                                                  <span className="text-[10px] text-gray-400 ml-0.5">{mi.plannedQuantityUnit || ''}</span>
                                                </td>
                                                <td className="px-2.5 py-2 text-right">
                                                  <span className={`font-bold ${(mi.totalConfirmedQuantity ?? 0) > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`}>
                                                    {mi.totalConfirmedQuantity ?? 0}
                                                  </span>
                                                </td>
                                              </tr>
                                            ))
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}

                              {sapPreviewData.confirmationGroups &&
                                sapPreviewData.confirmationGroups.some(cg => cg.materialOutputs?.length > 0) && (
                                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-600 overflow-hidden shadow-sm">
                                    <div className="px-3 py-2 bg-gradient-to-r from-emerald-500 to-green-500 flex items-center justify-between">
                                      <h4 className="font-semibold text-white flex items-center gap-2 text-sm">
                                        <Package className="w-4 h-4" />
                                        {t('pages.productionTasks.materialOutputs')}
                                      </h4>
                                      <span className="text-xs text-white bg-white/20 w-5 h-5 rounded-full flex items-center justify-center font-bold">
                                        {sapPreviewData.confirmationGroups.reduce((acc, cg) => acc + (cg.materialOutputs?.length || 0), 0)}
                                      </span>
                                    </div>
                                    <div className="overflow-x-auto max-h-48 overflow-y-auto">
                                      <table className="w-full text-xs">
                                        <thead className="bg-green-50 dark:bg-green-950 sticky top-0">
                                          <tr className="border-b border-green-100 dark:border-green-800">
                                            <th className="text-left px-2.5 py-1.5 text-green-600 dark:text-green-400 font-semibold uppercase tracking-wider text-[10px]">{t('pages.productionTasks.product')}</th>
                                            <th className="text-left px-2.5 py-1.5 text-green-600 dark:text-green-400 font-semibold uppercase tracking-wider text-[10px]">{t('pages.productionTasks.description')}</th>
                                            <th className="text-right px-2.5 py-1.5 text-green-600 dark:text-green-400 font-semibold uppercase tracking-wider text-[10px]">{t('pages.productionTasks.planned')}</th>
                                            <th className="text-right px-2.5 py-1.5 text-green-600 dark:text-green-400 font-semibold uppercase tracking-wider text-[10px]">{t('pages.productionTasks.confirmed')}</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50">
                                          {sapPreviewData.confirmationGroups.flatMap((cg, cgIndex) =>
                                            (cg.materialOutputs || []).map((mo, moIndex) => (
                                              <tr key={`mo-${cgIndex}-${moIndex}`} className="hover:bg-green-50/30 transition-colors">
                                                <td className="px-2.5 py-2">
                                                  <span className="font-bold text-emerald-600 dark:text-emerald-400 font-mono">
                                                    {mo.productId || '-'}
                                                  </span>
                                                </td>
                                                <td className="px-2.5 py-2 text-gray-600 dark:text-gray-400 max-w-[100px]">
                                                  <span className="block truncate" title={mo.productDescription}>
                                                    {mo.productDescription || '-'}
                                                  </span>
                                                </td>
                                                <td className="px-2.5 py-2 text-right">
                                                  <span className="font-semibold text-gray-700 dark:text-gray-300">{mo.plannedQuantity ?? '-'}</span>
                                                  <span className="text-[10px] text-gray-400 ml-0.5">{mo.plannedQuantityUnit || ''}</span>
                                                </td>
                                                <td className="px-2.5 py-2 text-right">
                                                  <span className={`font-bold ${(mo.totalConfirmedQuantity ?? 0) > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-orange-500 dark:text-orange-400'}`}>
                                                    {mo.totalConfirmedQuantity ?? 0}
                                                  </span>
                                                </td>
                                              </tr>
                                            ))
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}

                              {(!sapPreviewData.confirmationGroups ||
                                !sapPreviewData.confirmationGroups.some(cg => cg.materialInputs?.length > 0)) && (
                                  <div className="bg-gradient-to-br from-gray-50 dark:from-gray-700 to-gray-100 dark:to-gray-700 rounded-xl p-4 border border-gray-200 dark:border-gray-600 flex flex-col items-center justify-center min-h-[80px]">
                                    <Boxes className="w-6 h-6 text-gray-300 mb-1" />
                                    <span className="text-xs text-gray-400 font-medium">{t('pages.productionTasks.noMaterialInputs')}</span>
                                  </div>
                                )}

                              {(!sapPreviewData.confirmationGroups ||
                                !sapPreviewData.confirmationGroups.some(cg => cg.materialOutputs?.length > 0)) && (
                                  <div className="bg-gradient-to-br from-gray-50 dark:from-gray-700 to-gray-100 dark:to-gray-700 rounded-xl p-4 border border-gray-200 dark:border-gray-600 flex flex-col items-center justify-center min-h-[80px]">
                                    <Package className="w-6 h-6 text-gray-300 mb-1" />
                                    <span className="text-xs text-gray-400 font-medium">{t('pages.productionTasks.noMaterialOutputs')}</span>
                                  </div>
                                )}
                            </div>

                            {sapPreviewData.productionTasks && sapPreviewData.productionTasks.length > 0 && (
                              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-600 overflow-hidden shadow-sm">
                                <div className="px-3 py-2 bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-between">
                                  <h4 className="font-semibold text-white flex items-center gap-2 text-sm">
                                    <Settings className="w-4 h-4" />
                                    {t('pages.productionTasks.productionTasks')}
                                  </h4>
                                  <span className="text-xs text-white bg-white/20 w-5 h-5 rounded-full flex items-center justify-center font-bold">
                                    {sapPreviewData.productionTasks.length}
                                  </span>
                                </div>
                                <div className="overflow-x-auto max-h-44 overflow-y-auto">
                                  <table className="w-full text-xs">
                                    <thead className="bg-amber-50/50 sticky top-0">
                                      <tr className="border-b border-amber-100">
                                        <th className="text-left px-2.5 py-1.5 text-amber-600 dark:text-amber-400 font-semibold uppercase tracking-wider text-[10px]">{t('pages.productionTasks.taskId')}</th>
                                        <th className="text-left px-2.5 py-1.5 text-amber-600 dark:text-amber-400 font-semibold uppercase tracking-wider text-[10px]">{t('pages.productionTasks.operation')}</th>
                                        <th className="text-left px-2.5 py-1.5 text-amber-600 dark:text-amber-400 font-semibold uppercase tracking-wider text-[10px]">{t('pages.productionTasks.activity')}</th>
                                        <th className="text-left px-2.5 py-1.5 text-amber-600 dark:text-amber-400 font-semibold uppercase tracking-wider text-[10px]">{t('pages.productionTasks.status')}</th>
                                        <th className="text-center px-2.5 py-1.5 text-amber-600 dark:text-amber-400 font-semibold uppercase tracking-wider text-[10px]">{t('pages.productionTasks.barcode')}</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                      {sapPreviewData.productionTasks.map((task, index) => (
                                        <tr key={task.productionTaskId || index} className="hover:bg-amber-50/30 transition-colors">
                                          <td className="px-2.5 py-2">
                                            <span className="font-bold text-amber-600 dark:text-amber-400 font-mono">{task.productionTaskId}</span>
                                          </td>
                                          <td className="px-2.5 py-2 text-gray-600 dark:text-gray-400 font-mono">{task.operationTypeCode || '-'}</td>
                                          <td className="px-2.5 py-2 text-gray-600 dark:text-gray-400">{task.operationActivityTypeCode || '-'}</td>
                                          <td className="px-2.5 py-2">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase ${task.processingStatusCode === '3' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 ring-1 ring-emerald-200 dark:ring-emerald-800' :
                                              task.processingStatusCode === '2' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 ring-1 ring-blue-200 dark:ring-blue-800' :
                                                'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 ring-1 ring-gray-200 dark:ring-gray-600'
                                              }`}>
                                              {task.processingStatusCode || '-'}
                                            </span>
                                          </td>
                                          <td className="px-2.5 py-2 text-center">
                                            {task.barcodeBase64 ? (
                                              <img
                                                src={`data:image/png;base64,${task.barcodeBase64}`}
                                                alt={`Task ${task.productionTaskId} barcode`}
                                                className="h-7 w-auto mx-auto"
                                              />
                                            ) : (
                                              <span className="text-gray-300">—</span>
                                            )}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}

                            {(!sapPreviewData.confirmationGroups || sapPreviewData.confirmationGroups.length === 0) &&
                              (!sapPreviewData.productionTasks || sapPreviewData.productionTasks.length === 0) && (
                                <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-8 text-center border-2 border-dashed border-gray-200 dark:border-gray-600">
                                  <Activity className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                                  <p className="text-gray-500 dark:text-gray-400">{t('pages.productionTasks.noTasksNoActivities')}</p>
                                </div>
                              )}
                          </div>
                        </div>
                      </div>

                      <div className="px-6 py-3 bg-gray-100 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>{t('pages.productionTasks.fetchedFromSap')}</span>
                        <span>{t('pages.productionTasks.generatedAt', { date: new Date().toLocaleString() })}</span>
                      </div>
                    </div>
                  </div>
                )}

                {!sapPreviewData && !sapPreviewLoading && !sapPreviewError && !(specResults && specResults.length > 0) && (
                  <div className="text-center py-16">
                    <div className={`w-24 h-24 rounded-3xl flex items-center justify-center mx-auto mb-6 ${lookupMode === 'taskId'
                      ? 'bg-gradient-to-br from-indigo-100 dark:from-indigo-900/30 to-blue-100 dark:to-blue-900/30'
                      : 'bg-gradient-to-br from-purple-100 dark:from-purple-900/30 to-pink-100 dark:to-pink-900/30'
                      }`}>
                      {lookupMode === 'taskId' ? (
                        <Clipboard className="w-12 h-12 text-indigo-400" />
                      ) : (
                        <FileText className="w-12 h-12 text-purple-400" />
                      )}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                      {lookupMode === 'taskId'
                        ? t('pages.productionTasks.enterTaskIdInfo')
                        : t('pages.productionTasks.enterOrderIdInfo')}
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-4">
                      {lookupMode === 'taskId'
                        ? t('pages.productionTasks.lookupSpecificFull')
                        : t('pages.productionTasks.findProductionManage')}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between p-6 bg-gray-50 dark:bg-gray-700 border-t border-gray-100 dark:border-gray-700">
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  <Eye className="w-4 h-4" />
                  <span>
                    {t('pages.productionTasks.lookingUpBy')}: <span className="font-semibold">
                      {lookupMode === 'taskId' ? t('pages.productionTasks.taskIdMode') : lookupMode === 'orderId' ? t('pages.productionTasks.orderIdMode') : t('pages.productionTasks.salesOrderMode')}
                    </span>
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  {sapPreviewData && (
                    <button
                      onClick={handleImportTask}
                      className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all flex items-center gap-2"
                    >
                      <Database className="w-4 h-4" />
                      {isAdmin ? t('pages.productionTasks.importToDatabase') : t('pages.productionTasks.importAndAssign')}
                    </button>
                  )}
                  <button
                    onClick={closeSapPreviewModal}
                    className="px-6 py-2.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-semibold hover:bg-gray-100 dark:hover:bg-gray-600 transition-all"
                  >
                    {t('commonUi.close')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <AssignTaskModal
          isOpen={isAssignModalOpen}
          onClose={() => { setIsAssignModalOpen(false); setAssignTaskData(null); }}
          productionLot={assignTaskData}
          onAssigned={handleTaskAssigned}
          cachedUsers={cachedUsers}
          cachedTeams={cachedTeams}
        />
      </div>
    </div >
  );
};

export default ProductionTasksPage;
